import { getFamilyTreeIdFromStorage } from "./headOfLineRole";
import { isSuperAdmin } from "./superAdminAccess";

/**
 * Backend `Plan.plan_type` (aligns with Sequelize / DB enum comment).
 * 1 mothers_side, 2 fathers_side, 3 both_sides, 4 lifetime_mother_promo,
 * 5 member_standard, 6 member_basic, 7 lifetime_father_promo, 8 lifetime_both_sides_promo
 */
export const SUBSCRIPTION_PLAN_TYPE = {
  MOTHERS_SIDE: 1,
  FATHERS_SIDE: 2,
  BOTH_SIDES: 3,
  LIFETIME_MOTHER_PROMO: 4,
  MEMBER_STANDARD: 5,
  MEMBER_BASIC: 6,
  LIFETIME_FATHER_PROMO: 7,
  LIFETIME_BOTH_SIDES_PROMO: 8,
};

/**
 * Backend `Plan.billing_cycle` (STRING in DB; values are typically numeric strings in JSON).
 * 1 monthly, 2 quarterly, 3 yearly, 4 lifetime
 */
export const SUBSCRIPTION_BILLING_CYCLE = {
  MONTHLY: 1,
  QUARTERLY: 2,
  YEARLY: 3,
  LIFETIME: 4,
};

/** Role payload from GET /head-of-line/:id (stored in loggedIn_user_role) */
export function getLoggedInUserRole() {
  try {
    return JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
  } catch {
    return {};
  }
}

/**
 * Tree admin flag for header Role filter + profile label.
 * Prefer `loggedIn_user_role.is_admin`; if missing, use top-level `is_admin` (set by head-of-line sync) so UI stays aligned when one write lands before the other.
 */
export function isLoggedInUserTreeAdminFromStorage() {
  try {
    const r = getLoggedInUserRole();
    if (r && typeof r === "object") {
      if (r.is_admin === true) return true;
      if (r.is_admin === false) return false;
    }
  } catch {
    /* ignore */
  }
  try {
    const raw = localStorage.getItem("is_admin");
    if (raw == null) return false;
    if (raw === "true") return true;
    if (raw === "false") return false;
    const parsed = JSON.parse(raw);
    return parsed === true;
  } catch {
    return false;
  }
}

/** Family tree id — canonical source is `loggedIn_user_role`; use for GET /subscription/plans?family_tree_id=… */
export function getStoredFamilyTreeId() {
  const id = getFamilyTreeIdFromStorage();
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

/**
 * GET /subscription/plans — sole endpoint the frontend uses to list plans (Settings + signup).
 * Always sends family_tree_id (empty string when none). Do not use /subscription/packages/all.
 */
export function getSubscriptionPlansApiUrl() {
  const id = getStoredFamilyTreeId();
  return `/subscription/plans?family_tree_id=${encodeURIComponent(id)}`;
}

/** Admin must have an active subscription to use app sections (non-admins unaffected). */
export function isAdminWithoutActiveSubscription() {
  if (isSuperAdmin()) return false;
  const r = getLoggedInUserRole();
  if (r?.is_admin !== true) return false;
  return r?.is_subscription !== true;
}

/** true, 1, "1", "true" — common API / localStorage quirks */
export function isTruthySubscriptionFlag(val) {
  return val === true || val === 1 || val === "1" || String(val).toLowerCase() === "true";
}

function isExplicitMemberRole(r) {
  if (!r || typeof r !== "object") return false;
  const admin = r.is_admin;
  if (admin === true || admin === "true" || admin === 1) return false;
  return admin === false || admin === "false" || admin === 0;
}

/** Member (not tree admin) who has not paid the line subscription for the current tree. */
export function isUserMemberWithoutLineSubscription() {
  if (isSuperAdmin()) return false;
  const r = getLoggedInUserRole();
  if (!isExplicitMemberRole(r)) return false;
  return !isTruthySubscriptionFlag(r?.is_subscription);
}

/** True when role is an explicit non-admin (User) — Settings still calls GET /subscription/plans for everyone; this only picks member vs admin plan sections in the UI. */
export function shouldFetchSubscriptionPlansWithFamilyTreeId() {
  const r = getLoggedInUserRole();
  return isExplicitMemberRole(r);
}

/** True only when logged-in user is admin and has active subscription in role payload. */
export function isAdminWithSubscriptionInRole() {
  const r = getLoggedInUserRole();
  return r?.is_admin === true && r?.is_subscription === true;
}

/**
 * Which root head-of-line options are allowed when starting the tree (Gen 1).
 * @returns {"mother_only" | "father_only" | "both"}
 */
export function getFamilyTreeHeadLineRestriction() {
  const r = getLoggedInUserRole();
  if (r?.is_subscription !== true) return "both";

  const plan = r?.subscription?.Plan;
  const type = Number(plan?.plan_type);
  const name = (plan?.name ?? "").toString().trim().toLowerCase();
  const pt = SUBSCRIPTION_PLAN_TYPE;

  if (Number.isFinite(type)) {
    if (type === pt.MOTHERS_SIDE || type === pt.LIFETIME_MOTHER_PROMO) return "mother_only";
    if (type === pt.FATHERS_SIDE || type === pt.LIFETIME_FATHER_PROMO) return "father_only";
    if (
      type === pt.BOTH_SIDES ||
      type === pt.LIFETIME_BOTH_SIDES_PROMO ||
      type === pt.MEMBER_STANDARD ||
      type === pt.MEMBER_BASIC
    ) {
      return "both";
    }
  }

  if (name === "both" || name.includes("both side")) return "both";
  if (name === "mother") return "mother_only";
  if (name === "father") return "father_only";

  return "both";
}

/** Sent as `tree_side` on import and root head-of-line APIs (matches backend expectation). */
export const TREE_SIDE_API_MOTHER = "Mother Side";
export const TREE_SIDE_API_FATHER = "Father Side";

export function isTreeSideApiValue(v) {
  return v === TREE_SIDE_API_MOTHER || v === TREE_SIDE_API_FATHER;
}

/** Mother-side-only plan: only the mother-side root line is available at tree start. */
export function isMotherSideOnlySubscriptionPlan() {
  return getFamilyTreeHeadLineRestriction() === "mother_only";
}

/** Legacy ?tab= query value (older bookmarks and emails). */
const SETTINGS_SUBSCRIPTION_TAB_LEGACY = "Subscription Management";

export const SETTINGS_SUBSCRIPTION_TAB = "Subscription";

/** Canonical route for Subscription settings (sidebar + payment/modal redirects). */
export const SETTINGS_SUBSCRIPTION_PATH = "/settings/subscription";

/**
 * Legacy helper: old code used `/settings${settingsSubscriptionSearch()}`. Prefer SETTINGS_SUBSCRIPTION_PATH.
 * @deprecated
 */
export function settingsSubscriptionSearch() {
  return `?tab=${encodeURIComponent(SETTINGS_SUBSCRIPTION_TAB)}`;
}

/** True when the user is already on Subscription settings (modals skip reopening when navigating here). */
export function isSubscriptionSettingsLocation(loc) {
  if (!loc?.pathname) return false;
  if (loc.pathname.startsWith("/settings/subscription")) return true;
  if (!loc.pathname.startsWith("/settings")) return false;
  const tab = new URLSearchParams(loc.search || "").get("tab") || "";
  return tab === SETTINGS_SUBSCRIPTION_TAB || tab === SETTINGS_SUBSCRIPTION_TAB_LEGACY;
}

/**
 * Clears head-of-line role payload and persisted tree ids when switching Admin/User tree filter in the header.
 * Next GET /head-of-line + my-family-trees repopulates storage for the new context.
 */
export function clearHeadOfLineRoleLocalStorageForTreeFilterChange() {
  try {
    localStorage.removeItem("loggedIn_user_role");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("familyTreeId");
    const raw = localStorage.getItem("userDetail");
    if (raw) {
      const u = JSON.parse(raw);
      if (u && typeof u === "object") {
        delete u.family_tree_id;
        delete u.familyTreeId;
        localStorage.setItem("userDetail", JSON.stringify(u));
      }
    }
  } catch (_) {
    /* ignore */
  }
  window.dispatchEvent(new Event("loggedInUserRoleUpdated"));
  window.dispatchEvent(new Event("localStorageChange"));
}

export const ADMIN_SUBSCRIPTION_MODAL_DISMISSED_KEY =
  "admin_subscription_reminder_dismissed_session";

/** Layout listens for this to open AdminSubscriptionRequiredModal (e.g. locked sidebar click). */
export const OPEN_ADMIN_SUBSCRIPTION_MODAL_EVENT = "openAdminSubscriptionModal";

export const USER_LINE_SUBSCRIPTION_MODAL_DISMISSED_KEY =
  "user_line_subscription_reminder_dismissed_session";

/** Appends " Plan" to subscription titles when not already present (e.g. "Monthly" → "Monthly Plan"). */
export function ensurePlanLabel(label) {
  const text = String(label || "").trim();
  if (!text || /\bplan$/i.test(text)) return text;
  return `${text} Plan`;
}

/** Header calls this after head-of-line sync when Role filter is User (member). */
export const OPEN_USER_LINE_SUBSCRIPTION_MODAL_EVENT = "openUserLineSubscriptionModal";
